import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	AsyncSelect,
	type AsyncSelectProps,
	Box,
	Button,
	Container,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	SelectInput,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { ListView, TSearchParams } from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo, useState } from "react"
import useSWR from "swr"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import { generatePath } from "../router-utils"
import { WalletBalanceCard } from "./BalanceCard"
import type { TWalletStats } from "./store"

type TTenantOption = { id: number; name: string }

type TIntervalType = "day" | "week" | "month"

function isIntervalType(value: unknown): value is TIntervalType {
	return value === "day" || value === "week" || value === "month"
}

/** Cross-tenant admin's tenant picker for the dashboard. */
function SelectCreditConsumptionTenants(
	props: Omit<AsyncSelectProps, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey="credit-consumption-tenants"
			fetch={(q) =>
				xhr
					.get("/subscription-wallet-credit-usages/tenants", {
						params: { q },
					})
					.then((resp) => resp.data.data)
			}
			{...props}
		/>
	)
}

type TByType = {
	type: string
	label: string
	credits: number
	count: number
}

type TStats = {
	range: { after: string | null; before: string | null }
	total_credits: number
	by_type: Array<TByType>
	wallet: TWalletStats | null
	recharged_total: number
}

type TLedgerRow = {
	id: number
	type:
		| "trip_quote_itinerary_builder"
		| "mail_message_parsings"
		| "mail_message_sends"
	label: string
	consumer_id: number
	credits: number
	created_at: string
	reference?: {
		subject: string | null
		name?: string | null
		from_name?: string | null
		message_id?: string | null
		trip_quote_id?: number
		trip_id?: number | null
		// Models the mail thread is linked to. `type` is the morph alias
		// ("trips", "trip_plan_requests", …); more types arrive over time.
		links?: Array<{ type: string; id: number }>
	} | null
}

type TLedgerParams = {
	page: number
	after: string
	before: string
	tenant_id?: number
	/** Filter the ledger to a single consumer_type (e.g. "mail_messages"). */
	type?: string
}

// Per morph-type deep-link, given the linked model id + a `q` to pre-filter the
// destination list (the message-id, so it lands on this exact message). Add an
// entry as new ModelHasThreads types become linkable; unmapped types fall back
// to the global mail-messages search below.
const LINK_BUILDERS: Record<string, (id: number, q?: string) => string> = {
	trips: (id, q) => {
		const base = generatePath("/trips/:tripId/emails", { tripId: String(id) })
		return q ? `${base}?q=${encodeURIComponent(q)}` : base
	},
}

/**
 * Deep-link for a ledger row: prefer the mail thread's linked model (a trip's
 * emails listing pre-filtered to this message, etc.); otherwise fall back to
 * the global mail-messages search — the same link the Mail Parsing page uses.
 * Returns null when there's nothing to link to (e.g. an orphaned consumer).
 */
function referenceLink(row: TLedgerRow): string | null {
	const ref = row.reference
	const q = ref?.message_id || ref?.subject || undefined
	for (const link of ref?.links || []) {
		const build = LINK_BUILDERS[link.type]
		if (build) return build(link.id, q)
	}
	if (q) {
		return `${generatePath("/mail-messages")}?q=${encodeURIComponent(q)}`
	}
	return null
}

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">{label}</Text>
			<Text fontSize="xl" fontWeight="semibold">
				{value}
			</Text>
		</Stack>
	)
}

function getReferenceLink(row: TLedgerRow) {
	const { type, reference } = row
	switch (type) {
		case "trip_quote_itinerary_builder":
			return reference?.trip_quote_id
				? `/trips/${reference.trip_id}/quotes/${reference.trip_quote_id}`
				: `/trips/${reference?.trip_id}`

		default:
			return referenceLink(row)
	}
}
/**
 * Credit-consumption dashboard: wallet balance + credits consumed in the
 * selected interval (day / week / month), split by consumer type (AI Parsing /
 * Email Send), and a paginated ledger. Backed by
 * /subscription-wallet-credit-usages.
 */
export function CreditConsumptionView({ type }: { type?: string } = {}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const { hasPermission } = useCheckPermissions()
	// A cross-tenant admin (no own tenant) picks a tenant to scope the view.
	const adminMode = !user?.tenant_id
	const canViewWallet = hasPermission(PERMISSIONS.MANAGE_TENANTS)
	const [tenant, setTenant] = useState<TTenantOption | null>(null)
	// Day / week / month interval selector, mirroring the Mail Parsing view.
	// Persist the interval + anchor date in the URL so it survives reloads.
	const [query, setQuery] = useLocationQuery<
		TSearchParams & { interval?: string; anchor?: string },
		TSearchParams & { interval?: string; anchor?: string }
	>()
	const [intervalType, setIntervalType] = useState<TIntervalType>(
		isIntervalType(query.interval) ? query.interval : "month"
	)
	const [cursor, setCursor] = useState<Date>(() =>
		startOf(
			query.anchor
				? parseDateFromQuery(query.anchor, "YYYY-MM-DD")
				: new Date(),
			intervalType
		)
	)
	useEffect(() => {
		setQuery({
			interval: intervalType,
			anchor: dateToQuery(cursor, "YYYY-MM-DD"),
		})
	}, [intervalType, cursor, setQuery])
	const after = startOf(cursor, intervalType)
	const before = endOf(cursor, intervalType)
	const range = useMemo(
		() => ({ after: dateToUTCString(after), before: dateToUTCString(before) }),
		[after, before]
	)

	const tenantId = adminMode && tenant ? tenant.id : undefined

	const [page, setPage] = useState(1)
	// Reset to the first page whenever the month or selected tenant changes.
	useEffect(() => setPage(1), [range.after, range.before, tenantId])
	const params: TLedgerParams = useMemo(
		() => ({
			page,
			after: range.after,
			before: range.before,
			tenant_id: tenantId,
			type,
		}),
		[page, range, tenantId, type]
	)

	const statsParams = useMemo(
		() => ({ ...range, tenant_id: tenantId, type }),
		[range, tenantId, type]
	)
	const { data: statsResp } = useSWR<{ data: TStats }>(
		["credit-consumption-stats", range.after, range.before, tenantId, type],
		() =>
			xhr
				.get("/subscription-wallet-credit-usages/stats", {
					params: statsParams,
				})
				.then((resp) => resp.data)
	)
	const stats = statsResp?.data

	const rangeLabel =
		intervalType === "month"
			? formatDate(after, "MMM YYYY")
			: intervalType === "day"
				? formatDate(after, "DD MMM YYYY")
				: `${formatDate(after, "DD MMM")} – ${formatDate(before, "DD MMM YYYY")}`

	return (
		<Container paddingY="3">
			<Stack gap="4">
				<Inline
					justifyContent="between"
					flexWrap="wrap"
					gap="3"
					alignItems="center"
				>
					<Heading>Credit Consumption</Heading>
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						{adminMode ? (
							<Box style={{ minWidth: "220px" }}>
								<SelectCreditConsumptionTenants
									value={tenant || undefined}
									onChange={(value) =>
										setTenant((value as TTenantOption) || null)
									}
									multiple={false}
									placeholder="All tenants"
								/>
							</Box>
						) : null}
						<Button
							onClick={() =>
								setCursor(
									startOf(subtractUnit(cursor, 1, intervalType), intervalType)
								)
							}
							title="Previous"
						>
							<Icons.ChevronDown rotate="90" />
						</Button>
						<Box>
							<SelectInput
								value={intervalType}
								onChange={(e) => {
									const type = (e.currentTarget.value ||
										"month") as TIntervalType
									setIntervalType(type)
									setCursor(startOf(new Date(), type))
								}}
							>
								<option value="month">Month</option>
								<option value="week">Week</option>
								<option value="day">Day</option>
							</SelectInput>
						</Box>
						<Button
							onClick={() =>
								setCursor(
									startOf(addUnit(cursor, 1, intervalType), intervalType)
								)
							}
							title="Next"
						>
							<Icons.ChevronDown rotate="270" />
						</Button>
					</Inline>
				</Inline>

				<Stack
					gap="4"
					bgColor="default"
					padding="4"
					rounded="md"
					borderWidth="1"
				>
					<Text fontWeight="semibold">{rangeLabel}</Text>
					<Inline gap="8" flexWrap="wrap" alignItems="start">
						<WalletBalanceCard
							wallet={stats?.wallet}
							adminTenantId={
								adminMode && canViewWallet && tenant ? tenant.id : undefined
							}
						/>
						<Stack gap="2">
							<Text fontWeight="semibold">Credits Used</Text>
							<Text fontSize="xl" fontWeight="semibold">
								{!stats ? "..." : stats.total_credits.toFixed(2)}
							</Text>
						</Stack>
						{/* Recharged (all-time) — hidden for now.
							<Stat
								label="Recharged (all-time)"
								value={stats.recharged_total.toFixed(2)}
							/>
							*/}
						{!type ? (
							<Inline gap="2" flexWrap="wrap">
								<Text fontWeight="semibold" fontSize="sm" color="muted">
									By type
								</Text>
								{!stats ? (
									<Text>...</Text>
								) : stats.by_type.length === 0 ? (
									<Text color="muted" fontSize="sm">
										No credit consumption in this month.
									</Text>
								) : (
									<Inline gap="8" flexWrap="wrap">
										{stats.by_type.map((t) => (
											<Stat
												key={t.type}
												label={`${t.label} (${t.count})`}
												value={t.credits.toFixed(2)}
											/>
										))}
									</Inline>
								)}
							</Inline>
						) : null}
					</Inline>
				</Stack>

				<ListView<TLedgerRow, TLedgerParams>
					params={params}
					pageKey={`credit-consumption-ledger${type ? `-${type}` : ""}${tenantId ? `-t${tenantId}` : ""}`}
					fetch={(api) =>
						api
							.get("/subscription-wallet-credit-usages", { params })
							.then((resp) => resp.data)
					}
					onPageChange={(next) => setPage(next)}
					removeContainerPadding
				>
					{({ items }) => (
						<Table
							headers={["Type", "Detail", "Credits", "When"]}
							bordered
							hover
							responsive
							alignCols={{ 2: "right" }}
							rows={items.map((row) => {
								const ref = row.reference
								const subject = ref?.subject || ref?.name || "(No Subject)"
								const link = getReferenceLink(row)
								return [
									<Text fontSize="sm">{row.label}</Text>,
									ref?.trip_id ? (
										<Link
											color="accent"
											fontWeight="semibold"
											to={generatePath("/trips/:tripId", {
												tripId: ref.trip_id.toString(),
											})}
										>
											<Stack>
												<Text>{subject}</Text>
												<Text fontSize="sm">
													{joinAttributes(`# ${ref.trip_id}`)}
												</Text>
											</Stack>
										</Link>
									) : (
										<Stack gap="1">
											{link ? (
												<Link to={link} fontWeight="semibold">
													{subject}
												</Link>
											) : (
												<Text fontSize="sm">{subject}</Text>
											)}
											{ref?.from_name ? (
												<Text fontSize="xs" color="muted">
													{ref.from_name}
												</Text>
											) : null}
										</Stack>
									),
									<Text fontSize="sm">{Number(row.credits).toFixed(2)}</Text>,
									<Text fontSize="sm" color="muted">
										<RelativeTime timestamp={row.created_at} />
									</Text>,
								]
							})}
						/>
					)}
				</ListView>
			</Stack>
		</Container>
	)
}
